import {useCallback} from 'react';
import PropTypes from 'prop-types';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {downloadCsvFromBlobUrl} from '@/helpers/blobCsvDownload';
import {Constants} from '@/utils/constants/commonConstants';
import {DATE_FORMAT_YYYYMMDD} from '@/utils/constants/globalConstants';
import {getFormattedDate} from '@/utils/commons';
import {
  PEPIRIS_TASK_NAMES,
  PERFECT_STORE_TARGET_TYPES
} from '@/utils/constants/perfectStoreConstants';
import {
  awardPointsToggleSchema,
  joinPerfectStoreToggleSchema
} from '../PerfectStoreSetUp/schemas';
import ReviewField from './ReviewField';
import {getStoreTargetFileName, toYesNo} from './reviewHelpers';

const formatDate = (value) =>
  getFormattedDate(value, false, '-', DATE_FORMAT_YYYYMMDD);

const getTaskMetric = (formData = {}) => {
  if (formData.taskName === PEPIRIS_TASK_NAMES.COOLER_PURITY) {
    return {
      label: 'Target Cooler Purity',
      value: formData.targetCoolerPurity
    };
  }

  if (
    formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CE ||
    formData.taskName === PEPIRIS_TASK_NAMES.OUT_OF_STOCK_CEDO_DO
  ) {
    return {
      label: 'Minimum Number of Distinct SKU',
      value: formData.minimumDistinctSku
    };
  }

  return null;
};

const PepIrisSetupReview = ({formData = {}}) => {
  const {downloadFileOnBlob} = useAzureBlob();
  const fileName = getStoreTargetFileName(formData);
  const showRewardDate = !formData?.awardPointsWhenTargetMet;
  const activeAndDisplaySame = !formData?.hasDisplayDate;
  const displayStartDate = activeAndDisplaySame
    ? formData?.activeStartDate
    : formData?.displayStartDate;
  const displayEndDate = activeAndDisplaySame
    ? formData?.activeEndDate
    : formData?.displayEndDate;
  const taskMetric = getTaskMetric(formData);

  const handleFileDownload = useCallback(() => {
    void downloadCsvFromBlobUrl({
      fileUrl: formData?.storeTargetFileUrl,
      templateType: Constants.PerfectStore,
      downloadFileOnBlob
    });
  }, [formData?.storeTargetFileUrl, downloadFileOnBlob]);

  return (
    <div className='flex flex-col gap-6' data-testid='pepiris-setup-review'>
      <div className='ml-4'>
        <p className='review-section-label mb-3'>Perfect Store Setup</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div className='pepiris-review-grid'>
          <ReviewField label='Target Type' value={formData?.targetType} />
          <ReviewField label='Task Name' value={formData?.taskName} />
          <ReviewField label='Name' value={formData?.name} />
        </div>
        {taskMetric && (
          <div className='pepiris-review-grid mt-4'>
            <ReviewField label={taskMetric.label} value={taskMetric.value} />
          </div>
        )}
      </div>

      <div className='ml-4'>
        <p className='review-section-label mb-3'>
          {formData?.targetType ||
            PERFECT_STORE_TARGET_TYPES.StoreExecutionTargetPepIris}
        </p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div>
          <div className='review_subtitle'>File</div>
          {formData?.storeTargetFileUrl ? (
            <button
              type='button'
              className='review-file-link'
              onClick={handleFileDownload}
              data-testid='pepiris-setup-file-link'>
              {fileName || 'Download file'}
            </button>
          ) : (
            <span
              className='review_title'
              data-testid='pepiris-setup-file-empty'>
              {fileName || '-'}
            </span>
          )}
        </div>
      </div>

      <div className='ml-4'>
        <p className='review-section-label mb-3'>Availability</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div className='pepiris-review-grid'>
          <ReviewField
            label='Active Start Date'
            value={formatDate(formData?.activeStartDate)}
          />
          <ReviewField
            label='Active End Date'
            value={formatDate(formData?.activeEndDate)}
          />
          {showRewardDate ? (
            <ReviewField
              label='Point Delivery Date'
              value={formatDate(formData?.rewardDate)}
            />
          ) : (
            <div aria-hidden='true' />
          )}
        </div>
        <div className='pepiris-review-grid mt-4'>
          <ReviewField
            label={awardPointsToggleSchema.label}
            value={toYesNo(formData?.awardPointsWhenTargetMet)}
          />
          <ReviewField
            label='Submission Date'
            value={formatDate(formData?.submissionDate)}
          />
          {formData?.hasValidationDate ? (
            <ReviewField
              label='Validation Date'
              value={formatDate(formData?.validationDate)}
            />
          ) : (
            <div aria-hidden='true' />
          )}
        </div>
        <div
          className='pepiris-review-grid mt-4'
          data-testid='pepiris-review-display-dates'>
          <ReviewField
            label='Display Start Date'
            value={formatDate(displayStartDate)}
          />
          <ReviewField
            label='Display End Date'
            value={formatDate(displayEndDate)}
          />
          <div aria-hidden='true' />
        </div>
        <div className='mt-4'>
          <ReviewField
            label={joinPerfectStoreToggleSchema.label}
            value={toYesNo(formData?.requiredUserToJoin)}
          />
        </div>
      </div>
    </div>
  );
};

PepIrisSetupReview.propTypes = {
  formData: PropTypes.object
};

export default PepIrisSetupReview;
